import { TABLE_ROWS } from 'common/constants';
import React, { useCallback } from 'react';
import { useStyles } from './index.styles';

import { IColumn, IconButton, Stack, Text, TooltipHost } from '@fluentui/react';
import {
  AttachablePayCycleDocuments,
  AttachablePayCycleDocuments_attachablePayCycleDocuments_nodes,
} from 'ap/paymentCycle/__generated__/AttachablePayCycleDocuments';
import { AttachPayCycleDocumentVariables } from 'ap/paymentCycle/__generated__/AttachPayCycleDocument';
import { DocumentDataCallout } from 'common/components/AttachDocumentModal/DocumentDataCallout';
import {
  dateConvertions,
  dateFormat,
  getGlobalDateFormat,
} from 'common/utils/dateFormats';
import { toOrderByVariable } from './utils';

import { PayCycleAvailableDocumentTypes } from 'ap/paymentCycle/__generated__/PayCycleAvailableDocumentTypes';
import { GetPayCycleDetails_payCycle } from 'ap/paymentCycle/view/__generated__/GetPayCycleDetails';
import { GenericAttachForm } from 'common/components/ActionMenu/Attach/AttachFormModal';
import { _columns } from 'common/components/ActionMenu/Attach/payment_columns_data';
import { DocumentViewModalState } from 'common/components/DocumentList';

import ATTACHABLE_PAYCYCLE_DOCUMENTS from '../../../../../AttachablePayCycleDocuments.graphql';
import ATTACH_PAYCYCLE_DOCUMENTS from '../../../../../AttachPayCycleDocument.graphql';
import PAYCYCLE_DETAILS from '../../../../GetPayCycleDetails.graphql';

type AttachableDataType =
  AttachablePayCycleDocuments_attachablePayCycleDocuments_nodes;
export interface AttachFormModalProps {
  paymentCycle: GetPayCycleDetails_payCycle;
  availableDocumentTypes: PayCycleAvailableDocumentTypes | undefined;
  dirty: boolean;
}

export const AttachFormModal: React.FC<AttachFormModalProps> = ({
  paymentCycle,
  availableDocumentTypes,
  dirty,
}) => {
  const styles = useStyles();

  const getDocumentTypeOptions = useCallback(
    (data: typeof availableDocumentTypes | undefined) => {
      return (
        data?.payCycleAvailableDocumentTypes?.nodes.map((item) => ({
          key: item.id,
          text: item.documentType!,
        })) || []
      );
    },
    []
  );

  const canSelectItem = useCallback(
    (item: AttachableDataType) =>
      paymentCycle?.isDraft ? true : !item._isAccountingDocument,
    [paymentCycle]
  );

  const getMutationInput = useCallback(
    (selectedItems: AttachableDataType[], entityId: string) => {
      const entityDocIds = selectedItems.map((item) => item.id);
      return {
        input: {
          payCycleId: entityId,
          entityDocumentId: entityDocIds,
        },
      } as AttachPayCycleDocumentVariables;
    },
    []
  );

  const onRenderItemColumn = useCallback(
    (
      item?: AttachableDataType,
      _index?: number,
      column?: IColumn,
      openDocumentViewModal?: (state: DocumentViewModalState) => void
    ) => {
      if (!item || !column) return undefined;
      const disableLogic = paymentCycle?.isDraft
        ? false
        : item._isAccountingDocument;
      const fieldContent = item[
        column.fieldName as keyof AttachableDataType
      ] as string | null;

      switch (column.key) {
        case 'fileReference':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <DocumentDataCallout disabled={disableLogic!} item={item} />
            </Stack>
          );
        case 'documentType':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {item.documentTypes?.documentType}
              </Text>
            </Stack>
          );
        case 'indexAmount':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <Text className={styles.contentColumnAlignRight}>
                {fieldContent}
              </Text>
            </Stack>
          );
        case 'isoCode':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {item.currency?.isoCode}
              </Text>
            </Stack>
          );
        case '_uploadDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {getGlobalDateFormat(item._uploadDate!)}
              </Text>
            </Stack>
          );
        case 'indexTransactionDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              {item.indexTransactionDate && (
                <Text className={disableLogic ? styles.disabledText : ''}>
                  {dateFormat(dateConvertions(item.indexTransactionDate))}
                </Text>
              )}
            </Stack>
          );
        case 'action':
          const viewDocumentVisible =
            item._isProtected! || item._fileViewer !== 'browser';
          return (
            <Stack
              className={styles.columnHeight}
              tokens={{ childrenGap: 10 }}
              horizontal
              verticalAlign="center"
            >
              <TooltipHost content="View" id="tooltipId">
                <IconButton
                  disabled={viewDocumentVisible}
                  iconProps={{ iconName: 'View' }}
                  onClick={() =>
                    openDocumentViewModal?.({
                      isOpen: true,
                      title: item.fileReference,
                      entityDocumentId: item.id,
                      _fileType: item._fileType!,
                    })
                  }
                />
              </TooltipHost>
            </Stack>
          );
        default:
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {fieldContent}
              </Text>
            </Stack>
          );
      }
    },
    [styles, paymentCycle]
  );

  const transformData = useCallback(
    (data: AttachablePayCycleDocuments | undefined) => {
      return data?.attachablePayCycleDocuments?.nodes;
    },
    []
  );

  return (
    <GenericAttachForm
      entity={{ id: paymentCycle?.id!, isDraft: paymentCycle?.isDraft }}
      availableDocumentTypes={availableDocumentTypes}
      attachableDocumentsQuery={ATTACHABLE_PAYCYCLE_DOCUMENTS}
      attachDocumentsMutation={ATTACH_PAYCYCLE_DOCUMENTS}
      detailsQuery={PAYCYCLE_DETAILS}
      columns={_columns}
      modalWidth={1350}
      isDirty={dirty}
      getDocumentTypeOptions={getDocumentTypeOptions}
      transformData={transformData}
      canSelectItem={canSelectItem}
      getMutationInput={getMutationInput}
      onRenderItemColumn={onRenderItemColumn}
      initialQueryVariables={{
        payCycleId: paymentCycle?.id,
        first: TABLE_ROWS,
      }}
      toOrderByVariable={toOrderByVariable}
    />
  );
};
